import { CartAddEvent } from '@theme/events';

const ADDED_FEEDBACK_MS = 2000;
const DESKTOP_BREAKPOINT = 750;

class ElixxProductAddons extends HTMLElement {
  /** @type {AbortController | null} */
  #abortController = null;

  /** @type {string} */
  #addLabel = 'Add to cart';

  /** @type {string} */
  #addedLabel = 'Added';

  /** @type {number} */
  #previousScrollY = 0;

  connectedCallback() {
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    this.#addLabel = this.dataset.addonAddLabel || this.#addLabel;
    this.#addedLabel = this.dataset.addonAddedLabel || this.#addedLabel;

    this.querySelectorAll('[data-elixx-addon]').forEach((row) => {
      if (row.hasAttribute('data-elixx-addon-selection')) {
        this.#bindSelectionRow(row, signal);
      } else {
        this.#bindRow(row, signal);
      }
    });
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    this.#abortController = null;
  }

  /** @param {Element} row @param {AbortSignal} signal */
  #bindSelectionRow(row, signal) {
    const trigger = row.querySelector('[data-addon-select-trigger]');
    const sheet = row.querySelector('[data-addon-select-sheet]');
    if (!(trigger instanceof HTMLButtonElement) || !(sheet instanceof HTMLDialogElement)) return;

    const commitBtn = sheet.querySelector('[data-addon-sheet-commit]');

    trigger.addEventListener('click', () => this.#openSheet(row, trigger, sheet), { signal });

    sheet.querySelectorAll('[data-addon-sheet-close]').forEach((closeBtn) => {
      closeBtn.addEventListener('click', () => this.#closeSheet(row, trigger, sheet), { signal });
    });

    sheet.addEventListener(
      'click',
      (event) => {
        if (event.target === sheet) this.#closeSheet(row, trigger, sheet);
      },
      { signal }
    );

    sheet.addEventListener(
      'cancel',
      (event) => {
        event.preventDefault();
        this.#closeSheet(row, trigger, sheet);
      },
      { signal }
    );

    commitBtn?.addEventListener(
      'click',
      (event) => {
        event.preventDefault();
        event.stopPropagation();
        this.#commitSheetSelections(row, trigger, sheet);
      },
      { signal }
    );

    row.querySelectorAll('[data-addon-option]').forEach((option) => {
      const revealBtn = option.querySelector('[data-addon-sheet-reveal]');
      const controls = option.querySelector('[data-addon-sheet-controls]');
      const input = option.querySelector('.elixx-product-addon__qty-input');
      const minus = option.querySelector('[data-qty-minus]');
      const plus = option.querySelector('[data-qty-plus]');

      revealBtn?.addEventListener(
        'click',
        (event) => {
          event.stopPropagation();
          this.#showOptionControls(option);
        },
        { signal }
      );

      minus?.addEventListener(
        'click',
        (event) => {
          event.stopPropagation();
          if (!(input instanceof HTMLInputElement)) return;
          const next = Math.max(0, Number(input.value) - 1);
          input.value = String(next);
          if (next === 0) {
            this.#hideOptionControls(option);
          }
          this.#updateSheetCommitState(sheet);
        },
        { signal }
      );

      plus?.addEventListener(
        'click',
        (event) => {
          event.stopPropagation();
          if (!(input instanceof HTMLInputElement)) return;
          const max = input.max ? Number(input.max) : Infinity;
          input.value = String(Math.min(max, Number(input.value) + 1));
          this.#updateSheetCommitState(sheet);
        },
        { signal }
      );

      input?.addEventListener(
        'change',
        () => {
          if (!(input instanceof HTMLInputElement)) return;
          const qty = Math.max(0, Number(input.value) || 0);
          input.value = String(qty);
          if (qty === 0) {
            this.#hideOptionControls(option);
          } else if (!option.classList.contains('is-configuring')) {
            this.#showOptionControls(option);
          }
          this.#updateSheetCommitState(sheet);
        },
        { signal }
      );
    });

    const repositionSheet = () => {
      if (sheet.open) this.#positionSheetPopover(trigger, sheet);
    };

    window.addEventListener('resize', repositionSheet, { signal });
    window.addEventListener('scroll', repositionSheet, { signal, passive: true });
  }

  /** @param {Element} option */
  #showOptionControls(option) {
    const available = option.getAttribute('data-available') === 'true';
    if (!available) return;

    const revealBtn = option.querySelector('[data-addon-sheet-reveal]');
    const controls = option.querySelector('[data-addon-sheet-controls]');
    const input = option.querySelector('.elixx-product-addon__qty-input');

    if (!(controls instanceof HTMLElement) || !(input instanceof HTMLInputElement)) return;

    if (Number(input.value) < 1) input.value = '1';

    option.classList.add('is-configuring');
    revealBtn?.classList.add('hidden');
    controls.classList.remove('hidden');

    const sheet = option.closest('[data-addon-select-sheet]');
    if (sheet instanceof HTMLDialogElement) this.#updateSheetCommitState(sheet);
  }

  /** @param {Element} option */
  #hideOptionControls(option) {
    const revealBtn = option.querySelector('[data-addon-sheet-reveal]');
    const controls = option.querySelector('[data-addon-sheet-controls]');
    const input = option.querySelector('.elixx-product-addon__qty-input');

    option.classList.remove('is-configuring');
    revealBtn?.classList.remove('hidden');
    controls?.classList.add('hidden');

    if (input instanceof HTMLInputElement) input.value = '0';

    const sheet = option.closest('[data-addon-select-sheet]');
    if (sheet instanceof HTMLDialogElement) this.#updateSheetCommitState(sheet);
  }

  /** @param {HTMLDialogElement} sheet */
  #updateSheetCommitState(sheet) {
    const commitBtn = sheet.querySelector('[data-addon-sheet-commit]');
    if (!(commitBtn instanceof HTMLButtonElement)) return;

    const hasSelections = this.#getSheetItems(sheet).length > 0;
    commitBtn.disabled = !hasSelections || commitBtn.classList.contains('is-adding');
  }

  /** @param {HTMLDialogElement} sheet */
  #getSheetItems(sheet) {
    /** @type {{ id: number; quantity: number }[]} */
    const items = [];

    sheet.querySelectorAll('[data-addon-option].is-configuring').forEach((option) => {
      const variantId = option.getAttribute('data-variant-id');
      const available = option.getAttribute('data-available') === 'true';
      const input = option.querySelector('.elixx-product-addon__qty-input');

      if (!variantId || !available || !(input instanceof HTMLInputElement)) return;

      const quantity = Math.max(0, Number(input.value) || 0);
      if (quantity > 0) {
        items.push({ id: Number(variantId), quantity });
      }
    });

    return items;
  }

  /** @param {Element} row @param {HTMLButtonElement} trigger @param {HTMLDialogElement} sheet */
  async #commitSheetSelections(row, trigger, sheet) {
    const commitBtn = sheet.querySelector('[data-addon-sheet-commit]');
    if (!(commitBtn instanceof HTMLButtonElement) || commitBtn.disabled || commitBtn.classList.contains('is-adding')) {
      return;
    }

    const items = this.#getSheetItems(sheet);
    if (!items.length) return;

    commitBtn.classList.add('is-adding');
    commitBtn.disabled = true;

    try {
      const data = await this.#addItemsToCart(items);

      this.#resetSheetOptions(sheet);
      this.#closeSheet(row, trigger, sheet);

      let itemCount = this.#getItemCountFromResponse(data);

      if (itemCount === null) {
        try {
          const cartResponse = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`);
          const cart = await cartResponse.json();
          itemCount = cart.item_count ?? 0;
        } catch {
          itemCount = 0;
        }
      }

      document.dispatchEvent(
        new CartAddEvent(data, this.dataset.sectionId || '', {
          source: 'elixx-product-addons',
          itemCount: itemCount ?? 0,
          sections: data.sections,
        })
      );
    } catch (error) {
      console.error('ELXX addons cart error:', error);
    } finally {
      commitBtn.classList.remove('is-adding');
      this.#updateSheetCommitState(sheet);
    }
  }

  /** @param {HTMLDialogElement} sheet */
  #resetSheetOptions(sheet) {
    sheet.querySelectorAll('[data-addon-option]').forEach((option) => {
      this.#hideOptionControls(option);
    });
  }

  /**
   * @param {Element} row
   * @param {HTMLButtonElement} trigger
   * @param {HTMLDialogElement} sheet
   */
  #openSheet(row, trigger, sheet) {
    if (sheet.open) return;

    this.#previousScrollY = window.scrollY;
    sheet.showModal();
    row.classList.add('is-sheet-open');
    trigger.setAttribute('aria-expanded', 'true');
    this.#positionSheetPopover(trigger, sheet);
    requestAnimationFrame(() => {
      this.#positionSheetPopover(trigger, sheet);
    });
    this.#updateSheetCommitState(sheet);
  }

  /**
   * @param {HTMLButtonElement} trigger
   * @param {HTMLDialogElement} sheet
   */
  #positionSheetPopover(trigger, sheet) {
    const isDesktop = window.matchMedia(`(min-width: ${DESKTOP_BREAKPOINT}px)`).matches;

    sheet.classList.toggle('elixx-product-addon__sheet--popover', isDesktop);

    sheet.style.removeProperty('top');
    sheet.style.removeProperty('left');
    sheet.style.removeProperty('right');
    sheet.style.removeProperty('bottom');
    sheet.style.removeProperty('inset');
    sheet.style.removeProperty('width');
    sheet.style.removeProperty('max-height');
    sheet.style.removeProperty('transform');

    if (!isDesktop) return;

    const triggerRect = trigger.getBoundingClientRect();
    const margin = 8;
    const viewportPadding = 16;
    const minWidth = 320;
    const maxWidth = 512;
    const sheetWidth = Math.min(
      maxWidth,
      Math.max(minWidth, triggerRect.width),
      window.innerWidth - viewportPadding * 2
    );

    let left = triggerRect.left;
    left = Math.max(viewportPadding, Math.min(left, window.innerWidth - sheetWidth - viewportPadding));

    const spaceBelow = window.innerHeight - triggerRect.bottom - margin;
    const spaceAbove = triggerRect.top - margin;
    const preferredMaxHeight = Math.min(520, window.innerHeight * 0.7);
    const maxHeight = Math.max(200, Math.min(preferredMaxHeight, Math.max(spaceBelow, spaceAbove) - margin));

    let top;
    if (spaceBelow >= spaceAbove) {
      top = triggerRect.bottom + margin;
    } else {
      top = Math.max(viewportPadding, triggerRect.top - margin - maxHeight);
    }

    sheet.style.inset = 'unset';
    sheet.style.right = 'auto';
    sheet.style.bottom = 'auto';
    sheet.style.width = `${sheetWidth}px`;
    sheet.style.maxHeight = `${maxHeight}px`;
    sheet.style.left = `${left}px`;
    sheet.style.top = `${top}px`;
    sheet.style.transform = 'none';
  }

  /**
   * @param {Element} row
   * @param {HTMLButtonElement} trigger
   * @param {HTMLDialogElement} sheet
   */
  #closeSheet(row, trigger, sheet) {
    if (sheet.open) {
      sheet.close();
    }

    sheet.removeAttribute('open');
    row.classList.remove('is-sheet-open');
    trigger.setAttribute('aria-expanded', 'false');
    sheet.classList.remove('elixx-product-addon__sheet--popover');
    sheet.style.removeProperty('top');
    sheet.style.removeProperty('left');
    sheet.style.removeProperty('right');
    sheet.style.removeProperty('bottom');
    sheet.style.removeProperty('inset');
    sheet.style.removeProperty('width');
    sheet.style.removeProperty('max-height');
    sheet.style.removeProperty('transform');
    window.scrollTo({ top: this.#previousScrollY, behavior: 'instant' });
  }

  /** @param {Element} row @param {AbortSignal} signal */
  #bindRow(row, signal) {
    const minus = row.querySelector('[data-qty-minus]');
    const plus = row.querySelector('[data-qty-plus]');
    const input = row.querySelector('.elixx-product-addon__qty-input');
    const addBtn = row.querySelector('[data-addon-add]');

    minus?.addEventListener(
      'click',
      () => {
        if (!(input instanceof HTMLInputElement)) return;
        const min = Number(input.min) || 1;
        input.value = String(Math.max(min, Number(input.value) - 1));
      },
      { signal }
    );

    plus?.addEventListener(
      'click',
      () => {
        if (!(input instanceof HTMLInputElement)) return;
        const max = input.max ? Number(input.max) : Infinity;
        input.value = String(Math.min(max, Number(input.value) + 1));
      },
      { signal }
    );

    addBtn?.addEventListener(
      'click',
      () => {
        this.#addRowToCart(row);
      },
      { signal }
    );
  }

  /** @returns {string[]} */
  #getSectionIds() {
    /** @type {string[]} */
    const sectionIds = [];
    document.querySelectorAll('cart-items-component').forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionIds.push(item.dataset.sectionId);
      }
    });
    return sectionIds;
  }

  /**
   * @param {{ item_count?: number, sections?: Record<string, string> }} data
   * @returns {number | null}
   */
  #getItemCountFromResponse(data) {
    if (typeof data.item_count === 'number') return data.item_count;

    const sections = data.sections;
    if (!sections || typeof sections !== 'object') return null;

    for (const html of Object.values(sections)) {
      if (typeof html !== 'string') continue;
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const countEl = doc.querySelector('[ref="cartItemCount"]');
      if (countEl?.textContent) {
        const count = parseInt(countEl.textContent, 10);
        if (!Number.isNaN(count)) return count;
      }
    }

    return null;
  }

  /**
   * @param {{ id: number; quantity: number }[]} items
   */
  async #addItemsToCart(items) {
    const sectionIds = this.#getSectionIds();
    const cartAddUrl =
      typeof Theme !== 'undefined' && Theme.routes?.cart_add_url
        ? Theme.routes.cart_add_url
        : `${window.Shopify?.routes?.root || '/'}cart/add.js`;

    /** @type {Record<string, unknown>} */
    const body = { items };

    if (sectionIds.length) {
      body.sections = sectionIds.join(',');
      body.sections_url = window.location.pathname;
    }

    const response = await fetch(cartAddUrl, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    });

    const data = await response.json();

    if (!response.ok || data.status) {
      throw new Error(data.description || data.message || 'Cart add failed');
    }

    return data;
  }

  /**
   * @param {string} variantId
   * @param {number} quantity
   * @param {HTMLButtonElement} addBtn
   */
  async #addVariantToCart(variantId, quantity, addBtn) {
    if (addBtn.disabled || addBtn.classList.contains('is-adding')) return;

    addBtn.classList.add('is-adding');
    addBtn.disabled = true;

    try {
      const data = await this.#addItemsToCart([{ id: Number(variantId), quantity }]);

      if (data.status) {
        console.error('ELXX addons cart error:', data.description || data.message);
        return;
      }

      let itemCount = this.#getItemCountFromResponse(data);

      if (itemCount === null) {
        try {
          const cartResponse = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`);
          const cart = await cartResponse.json();
          itemCount = cart.item_count ?? 0;
        } catch {
          itemCount = 0;
        }
      }

      document.dispatchEvent(
        new CartAddEvent(data, this.dataset.sectionId || '', {
          source: 'elixx-product-addons',
          itemCount: itemCount ?? 0,
          sections: data.sections,
        })
      );

      this.#showAddedState(addBtn);
    } catch (error) {
      console.error('ELXX addons cart error:', error);
    } finally {
      if (!addBtn.classList.contains('is-added')) {
        addBtn.classList.remove('is-adding');
        const row = addBtn.closest('[data-elixx-addon]');
        addBtn.disabled = row?.getAttribute('data-available') !== 'true';
      }
    }
  }

  /** @param {Element} row */
  async #addRowToCart(row) {
    const addBtn = row.querySelector('[data-addon-add]');
    if (!(addBtn instanceof HTMLButtonElement) || addBtn.disabled || addBtn.classList.contains('is-adding')) {
      return;
    }

    const variantId = row.getAttribute('data-variant-id');
    const available = row.getAttribute('data-available') === 'true';
    const input = row.querySelector('.elixx-product-addon__qty-input');

    if (!available || !variantId) return;

    const quantity = input instanceof HTMLInputElement ? Math.max(1, Number(input.value) || 1) : 1;
    await this.#addVariantToCart(variantId, quantity, addBtn);
  }

  /** @param {HTMLButtonElement} addBtn */
  #showAddedState(addBtn) {
    const iconWrapper = addBtn.querySelector('.svg-wrapper');
    if (!iconWrapper) return;

    const originalHtml = iconWrapper.innerHTML;
    const checkmarkSvg = this.#checkmarkSvg;

    addBtn.classList.remove('is-adding');
    addBtn.classList.add('is-added');
    addBtn.setAttribute('aria-label', this.#addedLabel);
    iconWrapper.innerHTML = checkmarkSvg;

    window.setTimeout(() => {
      addBtn.classList.remove('is-added');
      iconWrapper.innerHTML = originalHtml;
      addBtn.setAttribute('aria-label', this.#addLabel);

      const row = addBtn.closest('[data-elixx-addon]');
      const available = row?.getAttribute('data-available') === 'true';
      addBtn.disabled = !available;
    }, ADDED_FEEDBACK_MS);
  }

  /** Cached checkmark SVG from theme asset */
  get #checkmarkSvg() {
    const template = document.getElementById('elixx-addon-checkmark-template');
    if (template instanceof HTMLTemplateElement) {
      return template.innerHTML;
    }
    return '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 12 12" width="12" height="12"><path stroke="#fff" stroke-width="1.5" d="M2 6l3 3 5-5"/></svg>';
  }
}

if (!customElements.get('elixx-product-addons')) {
  customElements.define('elixx-product-addons', ElixxProductAddons);
}
